'use strict';
import isString from 'lodash/isString';
import map from 'lodash/map';
import {getLink} from '../../core-utilities/domain/ReduxUtils';


export const PROJECT_CLASS_NAME = 'ProjectDomain';
export const PROJECT_COLLECTION = 'projects';

/**
 * @param {String} projectId
 * @param {String} assetId
 * @return {?String}
 */
export function constructAssetProjectId(projectId, assetId) {
  return isString(assetId) && isString(projectId) ? assetId + projectId : null;
}

/**
 * @param {String} projectId
 * @param {String} assetProjectId
 * @return {?String}
 */
export function deconstructAssetId(projectId, assetProjectId) {
  return isString(assetProjectId) && isString(projectId) ? assetProjectId.substring(0, projectId.length) : null;
}

/**
 * @param {String} assetId
 * @param {String} assetProjectId
 * @return {?String}
 */
export function deconstructProjectId(assetId, assetProjectId) {
  return isString(assetProjectId) && isString(assetId) ? assetProjectId.substring(assetId.length) : null;
}

/**
 * @param {Object} data
 * @param {String} data.name
 * @return {?String}
 */
export function toStringStatic(data) {
  return data ? data.name : null;
}

/**
 * @typedef {Object} ProjectDomain
 * @property {String} name
 * @property {String} description
 * @property {Boolean} isPrivate
 * @property {String} status
 * @property {Date} completedOn
 * @property {String} completedBy
 * @property {Date} cancelledOn
 * @property {String} cancelledBy
 * @property {String} locationOrientation
 * @property {DomainLink} organization
 * @property {DomainLink} createdBy
 * @property {Date} dateCreated
 * @property {Date} lastUpdated
 */

/**
 * ProjectDomain angular provider
 * @constructor
 */
export function ProjectDomainProvider() {
  return {
    $get: [
      '$injector',
      'FirebaseUtil',
      'AuthService',
      'AbstractDomainClass',
      'MetricMixin',
      'OrgMixin',
      'FeedableMixin',
      'FirebaseConnectionService',
      'restUtils',
      'FirebaseQueueService',
      'ROOT_API_COLLECTION',
      'ROOT_API_SPEC',
      function (
        $injector,
        FirebaseUtil,
        AuthService,
        AbstractDomainClass,
        MetricMixin,
        OrgMixin,
        FeedableMixin,
        FirebaseConnectionService,
        restUtils,
        FirebaseQueueService,
        ROOT_API_COLLECTION,
        ROOT_API_SPEC,
      ) {

        /**
         * @param {Object} data Serialized data
         * @class
         * @implements {HasMembersDomainClass}
         */
        function ProjectDomain(data) {
          AbstractDomainClass.call(this, ProjectDomain, data);
        }

        AbstractDomainClass.__inheritFrom(ProjectDomain, PROJECT_CLASS_NAME, PROJECT_COLLECTION);
        OrgMixin(ProjectDomain);
        MetricMixin(ProjectDomain);
        FeedableMixin(ProjectDomain);

        ProjectDomain.$constraints.addConstraints({
          name: {type: 'string'},
          description: {type: 'string', widget: 'textarea', nullable: true},
          isPrivate: {type: 'boolean', nullable: true, defaultValue: false},
          status: {
            type: 'string',
            inList: ['cancelled', 'pending', 'in_progress', 'has_rejections', 'complete'],
            defaultValue: 'pending'
          },
          completedOn: {type: 'Date', immutable: true, nullable: true},
          completedBy: {type: 'string', immutable: true, nullable: true},
          cancelledOn: {type: 'Date', immutable: true, nullable: true},
          cancelledBy: {type: 'string', immutable: true, nullable: true},
          locationOrientation: {type: 'string', inList: ['none', 'single', 'many'], defaultValue: 'many'},
          organization: {type: 'Domain', domainClass: 'OrganizationDomain', belongsTo: true, links: 'projects'},
          createdBy: {type: 'DomainLink', domainClass: 'UserDomain', nullable: true},
          dateCreated: {type: 'Date'},
          lastUpdated: {type: 'Date'},

          // Override Camera Settings
          overrideCameraSettings: {type: 'boolean', nullable: true},
          cameraResolution: {type: 'number', nullable: true},
          jpgQuality: {type: 'number', nullable: true},
          vidQuality: {type: 'number', nullable: true},

          conversation: {type: 'LinkedDocument', domainClass: 'ConversationDomain', link: 'target'},

          members: {type: 'DomainCollection', domainClass: 'UserDomain', links: 'private-projects'},
          membersPersonnel: {type: 'DomainCollection', domainClass: 'PersonDomain', links: 'private-projects'},
          membersTeams: {type: 'DomainCollection', domainClass: 'TeamDomain', links: 'private-projects'},

          tasks: {type: 'DomainCollection', domainClass: 'TaskDomain', link: 'project', belongsTo: true},
          privateTasks: {type: 'DomainCollection', domainClass: 'TaskDomain', link: 'project', belongsTo: true},
          milestones: {type: 'DomainCollection', domainClass: 'MilestoneDomain', link: 'project', belongsTo: true},
          assetProjects: {type: 'DomainCollection', domainClass: 'AssetProjectDomain', link: 'project', belongsTo: true}
        });

        // for fallback API methods
        ProjectDomain.fallbackIdProperty = 'projectId';
        ProjectDomain.createFallbackMethod = 'projects/create';
        ProjectDomain.updateFallbackMethod = 'projects/update';
        ProjectDomain.updateStatusFallbackMethod = 'projects/status/update';

        // more methods go here;

        ProjectDomain.clone = function clone(sourceProjectId, targetProjectId) {
          const request = {
            method: 'clone_project',
            sourceProjectId: sourceProjectId,
            targetProjectId: targetProjectId
          };

          return FirebaseQueueService.addTask(request, AuthService.getUserId(), ROOT_API_COLLECTION, ROOT_API_SPEC);
        };

        ProjectDomain.constructAssetProjectId = constructAssetProjectId;

        ProjectDomain.deconstructAssetId = deconstructAssetId;

        ProjectDomain.deconstructProjectId = deconstructProjectId;

        ProjectDomain.prototype.create = restUtils.createProto(ProjectDomain, {
          getBody: project => ({
            name: project.name,
            description: project.description,
            isPrivate: project.isPrivate,
            locationOrientation: project.locationOrientation,
            orgId: project.organization && project.organization.$id,
          }),
        });

        ProjectDomain.prototype.update = restUtils.updateProto(ProjectDomain);

        ProjectDomain.updateStatus = restUtils.updateStatus(ProjectDomain);
        ProjectDomain.prototype.updateStatus = restUtils.updateStatusProto(ProjectDomain);
        ProjectDomain.prototype.getAssetProject = getAssetProjectProto;
        ProjectDomain.prototype.addAsset = addAssetProto;
        ProjectDomain.prototype.addAssets = addAssetsProto;
        ProjectDomain.prototype.removeAsset = removeAssetProto;
        ProjectDomain.prototype.removeAssets = removeAssetsProto;
        ProjectDomain.changePersonRole = changePersonRoleStatic;
        ProjectDomain.prototype.changePersonRole = changePersonRoleProto;
        ProjectDomain.changeTeamRole = changeTeamRoleStatic;
        ProjectDomain.prototype.changeTeamRole = changeTeamRoleProto;
        ProjectDomain.removePersonRole = removePersonRoleStatic;
        ProjectDomain.prototype.removePersonRole = removePersonRoleProto;
        ProjectDomain.removeTeamRole = removeTeamRoleStatic;
        ProjectDomain.prototype.removeTeamRole = removeTeamRoleProto;

        ProjectDomain.prototype.toString = toStringProto;

        function getAssetProjectProto(assetId) {
          const AssetProjectDomain = $injector.get('AssetProjectDomain');
          //console.log('ProjectDomain: getAssetProject', assetId);

          return AssetProjectDomain.$get(ProjectDomain.constructAssetProjectId(this.$id, assetId));
        }

        function addAssetProto(assetId) {
          const self = this;
          const request = {
            method: 'projects/assets/add',
            orgId: getLink(this, 'organization').id,
            assetId: assetId,
            projectId: this.$id,
            copyTasks: true
          };

          return FirebaseQueueService.addTask(request, AuthService.getUserId(), ROOT_API_COLLECTION, ROOT_API_SPEC).then(function () {
            //console.log('ProjectDomain: added asset. Fetching result.');
            return self.getAssetProject(assetId);
          });
        }

        function addAssetsProto(adding) {
          // TODO convert to a single method that sends the array to the server
          const project = this;
          return Promise.resolve(map(adding, eachId));

          function eachId(assetId) {
            return project.addAsset(assetId);
          }
        }

        function removeAssetProto(assetId) {
          const AssetProjectDomain = $injector.get('AssetProjectDomain');
          //console.log('ProjectDomain: getAssetProject', assetId);

          return AssetProjectDomain.$remove(ProjectDomain.constructAssetProjectId(this.$id, assetId));
        }

        function removeAssetsProto(removing) {
          // TODO convert to a single method that sends the array to the server
          const projectId = this.$id;
          const AssetProjectDomain = $injector.get('AssetProjectDomain');
          return Promise.resolve(map(removing, eachId));

          function eachId(assetId) {
            return AssetProjectDomain.$remove(ProjectDomain.constructAssetProjectId(projectId, assetId));
          }
        }

        function changePersonRoleProto(personId, role) {
          return changePersonRoleStatic(this.$id, personId, role);
        }

        function changePersonRoleStatic(id, personId, role) {
          const request = {
            method: 'projects/members/change-person',
            project: id,
            targetProfile: personId,
            role: role
          };

          return FirebaseQueueService.addTask(request, AuthService.getUserId(), ROOT_API_COLLECTION, ROOT_API_SPEC);
        }

        function changeTeamRoleProto(teamId, role) {
          return changeTeamRoleStatic(this.$id, teamId, role);
        }

        function changeTeamRoleStatic(id, teamId, role) {
          const request = {
            method: 'projects/members/change-team',
            project: id,
            targetTeam: teamId,
            role: role
          };

          return FirebaseQueueService.addTask(request, AuthService.getUserId(), ROOT_API_COLLECTION, ROOT_API_SPEC);
        }

        function removePersonRoleProto(personId) {
          return removePersonRoleStatic(this.$id, personId);
        }

        function removePersonRoleStatic(id, personId) {
          const request = {
            method: 'projects/members/remove-person',
            project: id,
            targetProfile: personId
          };

          return FirebaseQueueService.addTask(request, AuthService.getUserId(), ROOT_API_COLLECTION, ROOT_API_SPEC);
        }

        function removeTeamRoleProto(teamId) {
          return removeTeamRoleStatic(this.$id, teamId);
        }

        function removeTeamRoleStatic(id, teamId) {
          const request = {
            method: 'projects/members/remove-team',
            project: id,
            targetTeam: teamId
          };

          return FirebaseQueueService.addTask(request, AuthService.getUserId(), ROOT_API_COLLECTION, ROOT_API_SPEC);
        }

        function toStringProto() {
          return toStringStatic(this);
        }

        return ProjectDomain;
      }]
  };
}



// WEBPACK FOOTER //
// ./src/js/projects/domain/ProjectDomain.js